import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Users, UserPlus, Mail, Loader2, Send, RotateCcw, Trash2, Clock, CheckCircle2, AlertTriangle, MailWarning } from 'lucide-react';
import api from '@/services/api';

/**
 * ModeratorsTab — per-chatbot moderator management (max 2 per bot).
 * Split out of ChatbotDetailPage in iter119. Purely presentational —
 * parent owns state, dialog visibility, and mutation handlers.
 */
export const ModeratorsTab = ({
  moderators,
  loadingModerators,
  setShowAddModeratorDialog,
  handleResendModeratorInvite,
  handleToggleModerator,
  handleRemoveModerator,
}) => {
  const [emailDeliveryEnabled, setEmailDeliveryEnabled] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/email-delivery-status')
      .then((r) => { if (!cancelled) setEmailDeliveryEnabled(!!r.data?.email_delivery_enabled); })
      .catch(() => { if (!cancelled) setEmailDeliveryEnabled(null); });
    return () => { cancelled = true; };
  }, []);

  // Live counters for owners: Active / Pending / Stale (>7d = likely-bounced).
  const now = Date.now();
  const stats = moderators.reduce(
    (acc, m) => {
      if (m.status === 'active') acc.active += 1;
      else if (m.status === 'pending') {
        acc.pending += 1;
        const invitedAt = m.invited_at ? new Date(m.invited_at).getTime() : null;
        if (invitedAt && now - invitedAt > 7 * 24 * 60 * 60 * 1000) acc.stale += 1;
      }
      return acc;
    },
    { active: 0, pending: 0, stale: 0 }
  );
  const hasAny = moderators.length > 0;
  return (
    <>
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Moderators
            </CardTitle>
            <p className="text-sm text-slate-600 mt-1">Add up to 2 moderators to help manage this AI agent</p>
          </div>
          <Button
            onClick={() => setShowAddModeratorDialog(true)}
            disabled={moderators.length >= 2}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-moderator-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Add Moderator
          </Button>
        </CardHeader>
        <CardContent>
          {emailDeliveryEnabled === false && (
            <div className="mb-4 flex items-start gap-3 p-3 rounded-md bg-amber-50 border border-amber-300" data-testid="smtp-off-warning">
              <MailWarning className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <div className="font-medium">Email delivery is off</div>
                <div className="opacity-90">
                  Invitation emails will be logged but not sent. Ask an admin to configure SMTP in
                  <span className="font-mono px-1"> Admin → SMTP</span>
                  so invitees actually receive the link.
                </div>
              </div>
            </div>
          )}
          {hasAny && !loadingModerators && (
            <div className="mb-4 grid grid-cols-3 gap-2" data-testid="invite-status-widget">
              <div className="flex items-center gap-2 p-2 rounded-md bg-emerald-50 border border-emerald-200" data-testid="invite-stat-active">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <div className="text-xs text-emerald-700">
                  <div className="font-semibold text-sm">{stats.active}</div>
                  <div className="opacity-80">Active</div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-md bg-amber-50 border border-amber-200" data-testid="invite-stat-pending">
                <Clock className="w-4 h-4 text-amber-600" />
                <div className="text-xs text-amber-700">
                  <div className="font-semibold text-sm">{stats.pending}</div>
                  <div className="opacity-80">Pending</div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-md bg-rose-50 border border-rose-200" data-testid="invite-stat-stale">
                <AlertTriangle className="w-4 h-4 text-rose-600" />
                <div className="text-xs text-rose-700">
                  <div className="font-semibold text-sm">{stats.stale}</div>
                  <div className="opacity-80">Stale &gt;7d</div>
                </div>
              </div>
            </div>
          )}
          {loadingModerators ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>
          ) : moderators.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 mb-2">No moderators yet</p>
              <p className="text-sm text-slate-500">
                Add moderators by email. They&apos;ll be able to manage this AI agent&apos;s knowledge base and settings.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {moderators.map((moderator) => (
                <div key={moderator.moderator_id} className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium">
                      {moderator.name ? moderator.name.charAt(0).toUpperCase() : <Mail className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{moderator.name || moderator.email}</p>
                      {moderator.name && (<p className="text-sm text-slate-500">{moderator.email}</p>)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      moderator.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                        : moderator.status === 'suspended' ? 'bg-slate-200 text-slate-600 border-slate-300'
                          : 'bg-amber-100 text-amber-700 border-amber-200'
                    }>
                      {moderator.status === 'active' ? 'Active' : moderator.status === 'suspended' ? 'Suspended' : 'Pending Invitation'}
                    </Badge>

                    {moderator.status === 'pending' && (
                      <Button size="sm" variant="outline" onClick={() => handleResendModeratorInvite(moderator.moderator_id)}
                        className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`moderator-resend-${moderator.moderator_id}`}>
                        <Send className="w-3.5 h-3.5 mr-1.5" /> Resend invitation
                      </Button>
                    )}

                    {moderator.status === 'suspended' && (
                      <Button size="sm" onClick={() => handleToggleModerator(moderator.moderator_id, moderator.status)}
                        className="h-8 bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`moderator-resume-${moderator.moderator_id}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Resume
                      </Button>
                    )}

                    {moderator.status === 'active' && (
                      <div className="flex items-center gap-1.5" title="Suspend">
                        <Switch
                          checked={true}
                          onCheckedChange={() => handleToggleModerator(moderator.moderator_id, moderator.status)}
                          data-testid={`moderator-toggle-${moderator.moderator_id}`}
                        />
                      </div>
                    )}

                    <Button variant="ghost" size="icon" onClick={() => handleRemoveModerator(moderator.moderator_id)}
                      className="text-slate-500 hover:text-red-600"
                      data-testid={`moderator-remove-${moderator.moderator_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {moderators.length > 0 && moderators.length < 2 && (
            <div className="mt-4 p-4 rounded-lg bg-blue-50 border border-blue-200">
              <p className="text-sm text-blue-700">You can add {2 - moderators.length} more moderator{2 - moderators.length > 1 ? 's' : ''}.</p>
            </div>
          )}

          {moderators.length >= 2 && (
            <div className="mt-4 p-4 rounded-lg bg-slate-100 border border-slate-200">
              <p className="text-sm text-slate-600">Maximum number of moderators (2) reached.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-slate-900 text-base">About Moderators</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-slate-600">
            <p><strong>What moderators can do:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>View and manage the AI Employee&apos;s knowledge base</li>
              <li>Update AI Employee settings and rules</li>
              <li>View analytics for this AI Employee</li>
              <li>Respond to and manage conversations</li>
            </ul>
            <p className="pt-2"><strong>What moderators cannot do:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Create new AI Employees</li>
              <li>Access billing information</li>
              <li>Add or remove other moderators</li>
              <li>Delete the AI Employee</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default ModeratorsTab;
